// Package Imports
import { z } from 'zod'

// Core Imports
import { api } from '@/core/lib/api'
import type { TResponse } from '@/core/types/axios'

// External Module Imports
import type { TPermission } from '@/modules/role/role.types'
import { normalizePermissionKey } from '@/modules/role/role.utils'

// Module Imports
import { userRoleEndpoints } from '@/modules/user-role/user-role.constants'
import type {
  TAssignUserRolesRequest,
  TCreateUserRoleRequest,
  TEditUserRoleRequest,
  TUserRole,
  TUserRoleAssignedUser,
  TUserRoleAssignedUsersQuery,
  TUserRoleAssignedUsersResponse,
  TUserRolesContext,
  TUserRolesContextQuery,
  TAutoGrants,
} from '@/modules/user-role/user-role.types'

// Filter-and-parse helper: drops items that fail the schema (matches the
// pre-Zod mapper semantics where invalid rows silently disappeared).
const filterValid = <T extends z.ZodTypeAny>(item: T) =>
  z.array(z.unknown()).transform((arr) =>
    arr
      .map((x) => item.safeParse(x))
      .filter((r) => r.success)
      .map((r) => r.data as z.infer<T>)
  )

const permissionSchema: z.ZodType<TPermission> = z
  .object({
    id: z.string(),
    name: z.unknown(),
    description: z.string().catch(''),
  })
  .transform((p) => {
    const normalized = normalizePermissionKey(p.name)
    return normalized
      ? { id: p.id, name: normalized, description: p.description }
      : null
  })
  .pipe(
    z.custom<TPermission>(
      (v): v is TPermission => v !== null && typeof v === 'object'
    )
  )

const roleSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().catch(''),
  is_system: z.boolean().catch(false),
  permissions: filterValid(permissionSchema).catch([]),
  assigned_user_count: z.number().catch(0),
})

const assignedUserSchema = z.object({
  id: z.string(),
  name: z.string().catch('-'),
  email: z.string().catch('-'),
})

const assignableUserRoleSchema = z.object({
  id: z.string(),
  name: z.string(),
})

const assignableUserSchema = z.object({
  id: z.string(),
  name: z.string().catch('-'),
  email: z.string().catch('-'),
  roles: filterValid(assignableUserRoleSchema).catch([]),
})

const paginationSchema = z.object({
  total: z.number().catch(0),
  page: z.number().catch(1),
  per_page: z.number().catch(10),
  total_page: z.number().catch(1),
})

const emptyUserRole: TUserRole = {
  id: '',
  name: '',
  description: '',
  is_system: false,
  permissions: [],
  assigned_user_count: 0,
}

export async function getAllUserPermissions(): Promise<TPermission[]> {
  const response = await api.get<TResponse<unknown>>(
    userRoleEndpoints.allPermissions
  )
  return filterValid(permissionSchema).parse(response.data?.data ?? [])
}

export async function getUserRoleList(): Promise<TUserRole[]> {
  const response = await api.get<TResponse<unknown>>(userRoleEndpoints.list)
  return filterValid(roleSchema).parse(response.data?.data ?? [])
}

export async function getUserRoleDetail(id: string): Promise<TUserRole> {
  const response = await api.get<TResponse<unknown>>(
    userRoleEndpoints.detail(id)
  )
  const result = roleSchema.safeParse(response.data?.data)
  return result.success ? result.data : emptyUserRole
}

export async function getUserRoleAssignedUsers(
  roleId: string,
  query: TUserRoleAssignedUsersQuery
): Promise<TUserRoleAssignedUsersResponse> {
  const response = await api.get<TResponse<unknown>>(
    userRoleEndpoints.assignedUsers(roleId, query)
  )
  const data: TUserRoleAssignedUser[] = filterValid(assignedUserSchema).parse(
    response.data?.data ?? []
  )
  const pagination = paginationSchema.parse(
    response.data?.meta?.pagination ?? {}
  )
  return { data, pagination }
}

export async function createUserRole(
  payload: TCreateUserRoleRequest
): Promise<TUserRole> {
  const response = await api.post<TResponse<unknown>>(
    userRoleEndpoints.create,
    payload
  )
  const result = roleSchema.safeParse(response.data?.data)
  return result.success ? result.data : emptyUserRole
}

export async function editUserRole(
  payload: TEditUserRoleRequest
): Promise<TUserRole> {
  const { id, ...requestBody } = payload
  const response = await api.put<TResponse<unknown>>(
    userRoleEndpoints.update(id),
    requestBody
  )
  const result = roleSchema.safeParse(response.data?.data)
  return result.success ? result.data : emptyUserRole
}

export async function deleteUserRole(id: string): Promise<string> {
  const response = await api.delete<string>(userRoleEndpoints.delete(id))
  return response.data
}

export async function assignUserRoles(
  payload: TAssignUserRolesRequest
): Promise<void> {
  await api.put(userRoleEndpoints.assign(payload.user_id), {
    role_ids: payload.role_ids,
  })
}

const contextRoleSchema = z.object({
  id: z.string(),
  name: z.string().catch('-'),
})

export async function getUserRolesContext(
  query: TUserRolesContextQuery = {}
): Promise<TUserRolesContext> {
  const response = await api.get<{
    players?: { data?: unknown[]; meta?: { pagination?: unknown } }
    roles?: unknown[]
  }>(userRoleEndpoints.context(query))

  const players = filterValid(assignableUserSchema).parse(
    response.data?.players?.data ?? []
  )
  const pagination = paginationSchema.parse(
    response.data?.players?.meta?.pagination ?? {}
  )
  const roles = filterValid(contextRoleSchema).parse(response.data?.roles ?? [])

  return {
    players,
    pagination: {
      total: pagination.total,
      page: pagination.page,
      per_page: pagination.per_page,
    },
    roles,
  }
}

export async function getUserRoleAutoGrants(id: string): Promise<TAutoGrants> {
  const response = await api.get<TResponse<TAutoGrants>>(
    userRoleEndpoints.autoGrants(id)
  )
  return response.data?.data ?? { engine_enabled: false, rules: [] }
}

export async function saveUserRoleAutoGrants(
  id: string,
  payload: TAutoGrants
): Promise<TAutoGrants> {
  const response = await api.put<TResponse<TAutoGrants>>(
    userRoleEndpoints.autoGrants(id),
    payload
  )
  return response.data?.data ?? { engine_enabled: false, rules: [] }
}
